import type { DocNode } from '@atlaskit/adf-schema/doc';

export const SOURCE_EXCERPT_LIMIT = 300;

const CARD_NODE_TYPES = new Set(['inlineCard', 'blockCard', 'embedCard']);
const BLOCK_CONTAINERS = new Set([
	'paragraph',
	'heading',
	'blockquote',
	'listItem',
	'tableCell',
	'tableHeader',
]);

type AdfNode = {
	type?: string;
	text?: string;
	content?: AdfNode[];
	attrs?: { url?: unknown };
	marks?: { type?: string; attrs?: { href?: unknown } }[];
};

const asUrl = (value: unknown): string | undefined =>
	typeof value === 'string' && value ? value : undefined;

const cardUrl = (node: AdfNode): string | undefined => {
	if (!CARD_NODE_TYPES.has(node.type ?? '')) {
		return undefined;
	}
	return asUrl(node.attrs?.url);
};

/**
 * `href` of the node's `link` mark, i.e. an ordinary text hyperlink. These are
 * smart links too: `collectSmartLinks` walks the ADF with link marks included,
 * so a bare `<a>` in prose becomes a connection row and needs an excerpt.
 */
const linkMarkUrl = (node: AdfNode): string | undefined =>
	asUrl(node.marks?.find((mark) => mark?.type === 'link')?.attrs?.href);

const readText = (node: AdfNode | undefined): string => {
	if (!node) {
		return '';
	}
	if (typeof node.text === 'string') {
		return node.text;
	}
	if (cardUrl(node)) {
		return '';
	}
	if (!Array.isArray(node.content)) {
		return '';
	}

	const parts: string[] = [];
	for (const child of node.content) {
		// ADF documents are bounded trees; this walk is linear in node count.
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		const text = readText(child);
		if (text) {
			parts.push(text);
		}
	}
	return parts.join(' ');
};

const collectLinkUrls = (node: AdfNode, urls: string[]): void => {
	const url = cardUrl(node) ?? linkMarkUrl(node);
	if (url) {
		urls.push(url);
	}
	if (!Array.isArray(node.content)) {
		return;
	}
	for (const child of node.content) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		collectLinkUrls(child, urls);
	}
};

const normalizeExcerpt = (text: string): string | undefined => {
	const excerpt = text.replace(/\s+/g, ' ').trim().slice(0, SOURCE_EXCERPT_LIMIT);
	return excerpt || undefined;
};

/**
 * Maps each smart-link URL on the page to the prose of the nearest enclosing
 * block (paragraph, heading, list item, or table cell). Covers both card nodes
 * and ordinary text hyperlinks (`link` marks), matching the URL set
 * `collectSmartLinks` builds. First occurrence wins so later duplicates keep the
 * sentence the reader saw first.
 */
export const extractSmartLinkSourceExcerptsFromAdf = (
	adf: DocNode | null | undefined,
): Map<string, string> => {
	const excerpts = new Map<string, string>();
	if (!adf) {
		return excerpts;
	}

	const visit = (node: AdfNode): void => {
		if (node.type && BLOCK_CONTAINERS.has(node.type)) {
			const urls: string[] = [];
			collectLinkUrls(node, urls);
			const excerpt = normalizeExcerpt(readText(node));
			if (excerpt) {
				for (const url of urls) {
					if (!excerpts.has(url)) {
						excerpts.set(url, excerpt);
					}
				}
			}
		}
		if (!Array.isArray(node.content)) {
			return;
		}
		for (const child of node.content) {
			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
			visit(child);
		}
	};

	visit(adf as AdfNode);
	return excerpts;
};
